import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Lock, User, AlertCircle, FileText, X } from 'lucide-react'

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState(null)
  const [showError, setShowError] = useState(false)
  const [isAttemptingLogin, setIsAttemptingLogin] = useState(false)
  const [loginSuccess, setLoginSuccess] = useState(false)
  const { login, isLoading, error, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const from = location.state?.from?.pathname || '/patients/list'
  
  // Navigate only on explicit login success
  useEffect(() => {
    if (loginSuccess && isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [loginSuccess, isAuthenticated, navigate, from])
  
  // Update local error when auth error changes
  useEffect(() => {
    if (error) {
      setLocalError(error)
      setShowError(true)
    }
  }, [error])
  
  // Clear error when user starts typing
  const handleUsernameChange = (e) => {
    setUsername(e.target.value)
    if (showError) {
      setShowError(false)
      setLocalError(null)
    }
  }
  
  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
    if (showError) {
      setShowError(false)
      setLocalError(null)
    }
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError(null)
    setShowError(false)
    setIsAttemptingLogin(true)
    setLoginSuccess(false)
    
    const result = await login(username, password)
    
    setIsAttemptingLogin(false)
    
    if (result.success) {
      setLoginSuccess(true) // Trigger navigation via useEffect
    } else {
      // Error will be shown via useEffect that watches error state
    }
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo & Title */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-lg p-3">
              <img src="/favicon.ico" alt="Epione Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-900">Epione Portal</h2>
          <p className="mt-2 text-gray-600">Sign in to access your portal</p>
        </div>
        
        {/* Login Card */}
        <div className="bg-white shadow-2xl rounded-2xl p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Message */}
            {showError && localError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg shadow-md animate-shake">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <AlertCircle size={20} className="mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm">Login Failed</p>
                      <p className="text-sm mt-1">{localError}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowError(false)
                      setLocalError(null)
                    }}
                    className="ml-3 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="text-gray-400" size={20} />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={handleUsernameChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    showError && localError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
            </div>
            
            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="text-gray-400" size={20} />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    showError && localError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          
          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p></p>
          </div>
        </div>
        
        {/* Version Info */}
        <div className="text-center text-xs text-gray-500">
          <p>Version 1.0.0 • © 2026 Techsharthi Consultancy Services India LLP </p>
        </div>
      </div>
    </div>
  )
}

export default Login
